import {
  FC,
  memo,
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import {
  Bar,
  BarChart,
  Customized,
  ReferenceArea,
  ReferenceLine,
  XAxis,
  YAxis,
} from 'recharts';

import type { YardDirection } from '@/api/generated.schemas';
import { ChartContainer } from '@/components/ui/chart';
import { SimulationYardMovesTooltip } from '@/routes/(app)/(scenarios)/(simulation)/components/simulation-yard-moves-tooltip';
import {
  columnAt,
  formatShiftDayTick,
  isYardDirection,
  OTHER_VISITS_KEY,
  placeTooltip,
  segmentLabelPlacement,
  segmentRect,
  type YardMovesRow,
} from '@/routes/(app)/(scenarios)/(simulation)/yard-moves.utils';
import {
  segmentStyle,
  type VesselNameById,
  type YardMovesPanel,
} from '@/routes/(app)/(scenarios)/(simulation)/yard-moves-chart.utils';

const CHART_MARGIN = { top: 4, right: 8, bottom: 0, left: 0 };
const Y_AXIS_WIDTH = 40;
const X_AXIS_HEIGHT = 30;
/** Plot area inset from the wrapper's left edge, and left + right combined. */
const PLOT_LEFT_PX = CHART_MARGIN.left + Y_AXIS_WIDTH;
const PLOT_INSET_X_PX = PLOT_LEFT_PX + CHART_MARGIN.right;
const TOOLTIP_OFFSET_PX = 12;

interface SegmentRef {
  rowIndex: number;
  visitId: string;
  direction: YardDirection;
}

/** Hovered segment plus the cursor where it was entered (wrapper-relative). */
interface HoveredSegment extends SegmentRef {
  x: number;
  y: number;
  containerWidth: number;
  /** Down to the bottom of the panel stack, so only the last panel flips up. */
  containerHeight: number;
}

/**
 * The slice of Recharts' internal chart state a `<Customized>` child receives:
 * the invisible `inTotal` bar gives each shift's x/width, the y axis its scale.
 * These are undocumented Recharts 2.x props (pinned to 2.15.4); Recharts 3
 * drops them, so this layer needs rework on upgrade.
 */
interface ChartInternals {
  formattedGraphicalItems?: Array<{
    item: { props: { dataKey?: unknown } };
    props: { data?: Array<{ x?: number; width?: number }> };
  }>;
  yAxisMap?: Record<string, { scale: (value: number) => number }>;
}

interface SegmentsLayerProps extends ChartInternals {
  panel: YardMovesPanel;
}

let warnedMissingInternals = false;

/**
 * Every visit segment of the panel as one `<rect>` (plus in-segment labels),
 * drawn in a single layer instead of one Recharts `<Bar>` per visit×direction.
 * `data-*` attributes let the panel's mouse handlers identify a segment.
 */
const SegmentsLayer = ({
  panel,
  formattedGraphicalItems,
  yAxisMap,
}: SegmentsLayerProps) => {
  const columns = formattedGraphicalItems?.find(
    (graphicalItem) => graphicalItem.item.props.dataKey === 'inTotal'
  )?.props.data;
  const yScale = Object.values(yAxisMap ?? {}).at(0)?.scale;
  if (columns === undefined || yScale === undefined) {
    if (import.meta.env.DEV && !warnedMissingInternals) {
      warnedMissingInternals = true;
      console.warn(
        'Yard moves: Recharts <Customized> internals missing, segments not drawn.'
      );
    }
    return null;
  }

  return (
    <g
      data-yard-moves-segments
      className='text-[10px]'>
      {panel.stacks.map((segments, rowIndex) => {
        const column = columns.at(rowIndex);
        if (typeof column?.x !== 'number' || typeof column.width !== 'number') {
          return null;
        }
        const { x, width } = column;
        const rowKey = panel.rows.at(rowIndex)?.shiftId ?? rowIndex;
        return (
          <g key={rowKey}>
            {segments.map(({ visitId, direction, value, y0, y1 }) => {
              const { top, height } = segmentRect(y0, y1, yScale);
              const style = segmentStyle(visitId, panel.highlightedVisitIds);
              // Muted (pale) segments get no label: white text is unreadable on them.
              const label =
                style.fillOpacity === 1
                  ? segmentLabelPlacement({
                      left: x,
                      top,
                      width,
                      height,
                      value,
                      visitId,
                    })
                  : null;
              return (
                <g key={`${visitId}|${direction}`}>
                  <rect
                    className={
                      visitId === OTHER_VISITS_KEY
                        ? undefined
                        : 'cursor-pointer'
                    }
                    data-row={rowIndex}
                    data-visit={visitId}
                    data-direction={direction}
                    x={x}
                    y={top}
                    width={width}
                    height={height}
                    {...style}
                  />
                  {label && (
                    <text
                      x={label.cx}
                      y={label.cy}
                      transform={
                        label.rotate
                          ? `rotate(-90 ${label.cx} ${label.cy})`
                          : undefined
                      }
                      textAnchor='middle'
                      dominantBaseline='central'
                      fill='#fff'
                      fillOpacity={0.9}
                      pointerEvents='none'>
                      {visitId}
                    </text>
                  )}
                </g>
              );
            })}
          </g>
        );
      })}
    </g>
  );
};

interface PanelChartProps {
  panel: YardMovesPanel;
  timezone: string;
  dayTicks: Array<string>;
}

/**
 * Memoised so hover state changes in the parent never re-render the chart.
 * The two transparent total bars only exist to give Recharts the sign-stacked
 * y domain and per-shift column geometry.
 */
const PanelChartInner: FC<PanelChartProps> = ({
  panel,
  timezone,
  dayTicks,
}) => (
  <ChartContainer
    config={{}}
    className='h-[180px] w-full'>
    <BarChart
      data={panel.rows}
      stackOffset='sign'
      margin={CHART_MARGIN}>
      <XAxis
        dataKey='date'
        height={X_AXIS_HEIGHT}
        ticks={dayTicks}
        tickFormatter={(iso: string) => formatShiftDayTick(iso, timezone)}
        tickLine={false}
        axisLine={false}
      />
      <YAxis
        width={Y_AXIS_WIDTH}
        tickLine={false}
        axisLine={false}
      />
      {panel.regions.map((region) => (
        <ReferenceArea
          key={region.x1}
          x1={region.x1}
          x2={region.x2}
          fill='var(--muted-foreground)'
          fillOpacity={region.fillOpacity}
        />
      ))}
      <ReferenceLine
        y={0}
        stroke='var(--foreground)'
        strokeWidth={2}
      />
      <Bar
        dataKey='inTotal'
        stackId='total'
        fill='none'
        isAnimationActive={false}
      />
      <Bar
        dataKey='outTotal'
        stackId='total'
        fill='none'
        isAnimationActive={false}
      />
      <Customized component={<SegmentsLayer panel={panel} />} />
    </BarChart>
  </ChartContainer>
);
const PanelChart = memo(PanelChartInner);

interface SimulationYardMovesPanelProps extends PanelChartProps {
  title: string;
  shiftEndById: Record<string, string>;
  vesselNameById: VesselNameById;
  onToggleVisit: (visitId: string) => void;
  /** Shift column hovered in any panel; every panel highlights it. */
  hoveredRowIndex: number | null;
  onHoverRow: (rowIndex: number | null) => void;
}

function segmentAt(
  target: EventTarget,
  rows: Array<YardMovesRow>
): SegmentRef | null {
  if (!(target instanceof Element)) return null;
  const rect = target.closest('rect[data-visit]');
  if (!(rect instanceof SVGRectElement)) return null;
  const { row, visit, direction } = rect.dataset;
  const rowIndex = Number(row);
  if (
    !row ||
    !Number.isInteger(rowIndex) ||
    rowIndex < 0 ||
    rows.at(rowIndex) === undefined ||
    visit === undefined ||
    !isYardDirection(direction)
  ) {
    return null;
  }
  return { rowIndex, visitId: visit, direction };
}

interface Size {
  width: number;
  height: number;
}

function positionTooltip(
  tooltip: HTMLElement,
  anchor: HoveredSegment,
  size: Size
): void {
  const { left, top } = placeTooltip({
    cursorX: anchor.x,
    cursorY: anchor.y,
    containerWidth: anchor.containerWidth,
    containerHeight: anchor.containerHeight,
    tooltipWidth: size.width,
    tooltipHeight: size.height,
    offset: TOOLTIP_OFFSET_PX,
  });
  tooltip.style.setProperty('left', `${left}px`);
  tooltip.style.setProperty('top', `${top}px`);
}

function isSameSegment(
  next: SegmentRef | null,
  current: SegmentRef | null
): boolean {
  if (next === null || current === null) return next === current;
  return (
    next.rowIndex === current.rowIndex &&
    next.visitId === current.visitId &&
    next.direction === current.direction
  );
}

/** One stacked bar chart (IN up, OUT down) for a move category, with hover tooltip and click-to-select. */
const SimulationYardMovesPanelInner: FC<SimulationYardMovesPanelProps> = ({
  title,
  panel,
  timezone,
  dayTicks,
  shiftEndById,
  vesselNameById,
  onToggleVisit,
  hoveredRowIndex,
  onHoverRow,
}) => {
  const [hovered, setHovered] = useState<HoveredSegment | null>(null);
  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const anchorRef = useRef<HoveredSegment | null>(null);
  const tooltipSizeRef = useRef<Size>({ width: 0, height: 0 });
  const hoveredRow =
    hovered === null ? undefined : panel.rows.at(hovered.rowIndex);
  const rowCount = panel.rows.length;

  // Place the tooltip from its last measured size; the ResizeObserver
  // re-places it before paint whenever its size changes. No synchronous
  // layout read: the app shell's `:has()` rule makes any DOM insertion
  // restyle the whole page, so a forced read would block the mousemove.
  useLayoutEffect(() => {
    anchorRef.current = hovered;
    if (tooltipRef.current !== null && hovered !== null) {
      positionTooltip(tooltipRef.current, hovered, tooltipSizeRef.current);
    }
  }, [hovered]);
  const observeTooltip = useCallback((tooltip: HTMLDivElement | null) => {
    tooltipRef.current = tooltip;
    if (tooltip === null) return;
    const observer = new ResizeObserver(([entry]) => {
      const box = entry?.borderBoxSize.at(0);
      if (box === undefined) return;
      tooltipSizeRef.current = { width: box.inlineSize, height: box.blockSize };
      if (anchorRef.current !== null) {
        positionTooltip(tooltip, anchorRef.current, tooltipSizeRef.current);
      }
    });
    observer.observe(tooltip);
    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <div>
      <h3 className='text-muted-foreground mb-1 text-sm font-medium'>
        {title}
      </h3>
      {/* Pointer-only enhancement: keyboard users select visits via the filter bar's multi-select. */}
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
      <div
        className='relative'
        onMouseMove={(event) => {
          const bounds = event.currentTarget.getBoundingClientRect();
          const cursorX = event.clientX - bounds.left;
          const rowIndex = columnAt(
            cursorX - PLOT_LEFT_PX,
            bounds.width - PLOT_INSET_X_PX,
            rowCount
          );
          if (rowIndex !== hoveredRowIndex) onHoverRow(rowIndex);
          const segment = segmentAt(event.target, panel.rows);
          // Keep the tooltip anchored where the segment was entered.
          if (isSameSegment(segment, hovered)) return;
          const stack =
            event.currentTarget.closest('[data-yard-moves-panels]') ??
            event.currentTarget;
          setHovered(
            segment && {
              ...segment,
              x: cursorX,
              y: event.clientY - bounds.top,
              containerWidth: bounds.width,
              containerHeight:
                stack.getBoundingClientRect().bottom - bounds.top,
            }
          );
        }}
        onMouseLeave={() => {
          setHovered(null);
          onHoverRow(null);
        }}
        onClick={(event) => {
          const segment = segmentAt(event.target, panel.rows);
          if (segment !== null && segment.visitId !== OTHER_VISITS_KEY) {
            onToggleVisit(segment.visitId);
          }
        }}>
        <PanelChart
          panel={panel}
          timezone={timezone}
          dayTicks={dayTicks}
        />
        {/* Drawn outside the memoised chart so a hover never re-renders the segment layer. */}
        {hoveredRowIndex !== null && (
          <div
            data-yard-moves-column-highlight
            className='bg-muted-foreground pointer-events-none absolute opacity-15'
            style={{
              top: CHART_MARGIN.top,
              bottom: X_AXIS_HEIGHT,
              left: `calc(${PLOT_LEFT_PX}px + (100% - ${PLOT_INSET_X_PX}px) * ${hoveredRowIndex / rowCount})`,
              width: `calc((100% - ${PLOT_INSET_X_PX}px) / ${rowCount})`,
            }}
          />
        )}
        {hovered !== null && hoveredRow !== undefined && (
          <div
            ref={observeTooltip}
            className='pointer-events-none absolute z-10 w-max'>
            <SimulationYardMovesTooltip
              row={hoveredRow}
              visitId={hovered.visitId}
              direction={hovered.direction}
              timezone={timezone}
              panel={panel}
              shiftEndById={shiftEndById}
              vesselNameById={vesselNameById}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export const SimulationYardMovesPanel = memo(SimulationYardMovesPanelInner);
